import { useContext, useEffect, useRef, useState } from 'react'
import {
  CustomerContext,
  SET_CUSTOMER_DATA,
  SET_SELECTED_CUSTOMER_DATA,
  SHOW_CUSTOMER_FORM,
} from './context/context'
import { NavContext } from '../../../context/global_context'
import { deleteRequest, getRequest } from '../../../services/base'
import InputText from '../../common/InputText'
import LinkWithStatus from '../../common/LinkWithStatus'
import Button from '../../common/Button'
import Tooltip from '../../common/tooltip'
import { Edit, FileExport, TrashAlt } from '@styled-icons/boxicons-solid'
import Pagination from '../../common/Pagination'
import NoRecordsFound from '../../common/NoRecordsFound'
import CustomerForm from './form'
import { downloadFileBlob } from '../../../utils/common'

const CustomerRow = (props) => {
  return (
    <div className="flex flex-row flex-wrap items-center bg-white w-full py-2 px-3 border-b border-tracer-dark-blue text-black">
      <div className="flex flex-row w-full">
        <div className="flex w-2/12 pl-10 items-center table_text">
          <Tooltip message={props?.name}>
            <div>{props?.name}</div>
          </Tooltip>
        </div>
        <div className="flex w-2/12 pl-6 items-center table_text">
          <Tooltip message={props?.business_timezone}>
            <div>{props.business_timezone}</div>
          </Tooltip>
        </div>
        <div className="flex w-2/12 pl-6 items-center table_text">
          <Tooltip message={props?.make}>
            <div>{props.make}</div>
          </Tooltip>
        </div>
        <div className="flex w-4/12 pl-6 items-center table_text">
          <Tooltip message={props?.vin_dependent_make_model_tags}>
            <div>{props.vin_dependent_make_model_tags}</div>
          </Tooltip>
        </div>

        <div className="flex w-2/12 pl-6 justify-end ">
          <div>
            <span
              onClick={() => {
                props.onEdit()
              }}
              className="cursor-pointer p-2"
            >
              <Edit size="2rem" color="#0189E9" />
            </span>
            <span
              onClick={() => {
                props.onDelete()
              }}
              className="cursor-pointer p-2"
            >
              <TrashAlt size="2rem" color="#0189E9" />
            </span>
            <span
              onClick={() => {
                props.exportJson()
              }}
              className="cursor-pointer p-2"
            >
              <FileExport size="2rem" color="#0189E9" />
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

const Customer = () => {
  const [state, dispatch] = useContext(CustomerContext)
  const [navState, navDispatch] = useContext(NavContext)
  const [searchValue, setSearchValue] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef(`/api/customer/?search=${searchValue}`)

  useEffect(() => {
    getCustomerData(true)
  }, [])

  const deleteCustomer = async (id, name) => {
    if (
      window.confirm(`Are you sure want to delete selected ${name}`) === true
    ) {
      let res = await deleteRequest(`/api/customer/${id}/`)
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: `Customer Deleted Successfully`,
        },
      })
      getCustomerData(false)
      dispatch({
        type: SET_SELECTED_CUSTOMER_DATA,
        data: {},
        showFormPopup: false,
      })
    }
  }

  const getCustomerData = async (showLoader = false) => {
    if (showLoader) {
      navDispatch({
        type: 'loader',
        data: {
          caption: 'Fetching Customer Data',
          showLoader: true,
        },
        notification: {},
      })
    }

    let res = await getRequest(currentUrl.current)
    dispatch({ type: SET_CUSTOMER_DATA, data: res.results })
    setPagination(res.pagination)
    if (showLoader) {
      navDispatch({
        type: 'loader',
        data: {},
        notification: {},
      })
    }
  }

  const downloadJson = (customer) => {
    if (
      window.confirm(`Are you sure want to download for ${customer.name}`) ===
      true
    ) {
      let JSON_DATA = {
        make: customer?.make,
        name: customer?.name,
        business_timezone: customer?.business_timezone,
        vin_dependent_make_model_tags:
          customer?.vin_dependent_make_model_tags.split(','),
      }
      downloadFileBlob(
        new Blob([JSON.stringify(JSON_DATA)], {
          type: 'application/json',
        }),
        `${customer.make}_make.json`
      )
    }
  }

  return (
    <div>
      {state?.showFormPopup && (
        <CustomerForm
          getCustomerData={() => {
            getCustomerData('', false)
          }}
        />
      )}

      <form
        className="flex flex-col w-full space-y-2 pb-5"
        acceptCharset="UTF-8"
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        <div className="flex space-x-5">
          <div className="flex w-6/12 font-light text-right space-x-5">
            <InputText
              name="search"
              value={searchValue}
              type="text"
              placeholder={`Enter To Search Customer`}
              onChange={(e) => {
                setSearchValue(e)
              }}
              wrapperClass={`p-0`}
              isFullSize={false}
              className="h-10 px-5 pr-5"
              isRequired={false}
            />
            <Button
              className={`bg-tracer-lt-blue font-light hover:bg-blue-700 text-white py-2 px-3 rounded`}
              onClick={() => {
                currentUrl.current = `/api/customer/?search=${searchValue}`
                getCustomerData(true)
              }}
              text="Search"
            />
          </div>
          <div className="flex w-6/12 font-light text-right space-x-5 justify-end">
            <LinkWithStatus
              href={`/administration/customer/new`}
              className={
                'cursor-pointer inline-block bg-tracer-lt-blue font-light hover:bg-blue-700 text-white py-2 px-3 rounded'
              }
              title="Create New Customer"
              onClickFunc={() => {
                dispatch({
                  type: SHOW_CUSTOMER_FORM,
                  data: {},
                  showFormPopup: true,
                })
              }}
            />
          </div>
        </div>
      </form>

      <div className="font-light">
        <div className="flex flex-row w-full bg-tracer-lt-blue rounded-t-lg text-white px-3 text-sm">
          <div className="flex w-2/12 pl-10 items-center">Name</div>
          <div className="flex w-2/12 pl-6 items-center">Timezone</div>
          <div className="flex w-2/12 pl-6 items-center">Make</div>
          <div className="flex w-4/12 pl-6 items-center">Tags</div>
          <div className="w-2/12 pl-6"></div>
        </div>
      </div>

      {state?.data?.map((customer, index) => {
        return (
          <CustomerRow
            key={`customer-${customer.id}-${index}`}
            {...customer}
            onEdit={() => {
              dispatch({
                type: SET_SELECTED_CUSTOMER_DATA,
                data: { ...customer, index: index },
                showFormPopup: true,
              })
            }}
            onDelete={() => {
              deleteCustomer(customer.id, customer.name)
            }}
            exportJson={() => {
              downloadJson(customer)
            }}
          />
        )
      })}
      {state?.data?.length < 1 && <NoRecordsFound />}
      <div className="py-6">
        <Pagination
          context={pagination}
          onClickNext={(e) => {
            currentUrl.current = e
            getCustomerData(true)
          }}
          onClickPrev={(e) => {
            currentUrl.current = e
            getCustomerData(true)
          }}
        />
      </div>
    </div>
  )
}

export default Customer
